// Visitor analytics DTOs.
// Mirrors the API JSON wire shape (PascalCase), consistent with every other DTO in this repo.
//
// ORB-328 (P2) shipped the baseline funnel counts (LandingPageViews / ThankYouPageViews /
// Conversions). ORB-373 (P3 · M1) layers on richer per-level metrics (unique known/unknown/
// returning visitors, sessions, conversion rate, top referrers/UTM campaigns, channel
// breakdowns), two attribution views, aggregate unknown reporting, and per-Contact timelines.
//
// The M1 additions on AnalyticsViewDto are optional so the P2 surfaces (which only read the
// three funnel metrics) keep compiling and rendering unchanged while the M1 API rolls out.

/**
 * A single analytics metric.
 *
 * `PreviousWindow` / `Delta` are populated only for range-aware scopes (Campaign /
 * Workspace). At the range-less Landing Page (Web) level the API returns `Current`
 * only, leaving these undefined.
 *
 * Rate metrics (e.g. conversion rate) carry a percentage in `Current` (0–100), not a count.
 */
export interface AnalyticsMetricDto {
  Current: number
  PreviousWindow?: number
  Delta?: number
}

/**
 * Attribution view selector (ORB-373). Flips how sessions/conversions are credited:
 *  - `ConversionSession` (default): the converting session's own first-touch
 *    (UTM → referrer → Direct).
 *  - `FirstSeen`: the visitor's first-ever session first-touch.
 */
export type AttributionView = 'ConversionSession' | 'FirstSeen'

export const ATTRIBUTION_VIEWS: { value: AttributionView; label: string }[] = [
  { value: 'ConversionSession', label: 'Conversion session' },
  { value: 'FirstSeen', label: 'First seen' },
]

/** Marketing channel buckets derived from UTM medium + referrer. */
export type AnalyticsChannel = 'Email' | 'Paid' | 'Social' | 'Organic' | 'Direct'

/**
 * One row in a top-N attribution list (top referrers, top UTM campaigns, sessions by
 * source/medium/campaign). `Sessions` is the primary count; `Conversions` is included
 * when the API credits conversions to the row.
 */
export interface AttributionItemDto {
  Label: string
  Sessions: AnalyticsMetricDto
  Conversions?: AnalyticsMetricDto
}

/** Sessions split across the five marketing channels. Each bucket is range-aware. */
export interface ChannelBreakdownDto {
  Email: AnalyticsMetricDto
  Paid: AnalyticsMetricDto
  Social: AnalyticsMetricDto
  Organic: AnalyticsMetricDto
  Direct: AnalyticsMetricDto
}

/**
 * Aggregate unknown-visitor reporting (ORB-373). Counts only — no individual unknown
 * profiles are ever surfaced. `ConversionRate.Current` is the unknown→known conversion
 * rate as a percentage (0–100).
 */
export interface UnknownReportingDto {
  UniqueVisitors: AnalyticsMetricDto
  Sessions: AnalyticsMetricDto
  ConversionRate: AnalyticsMetricDto
}

/**
 * One discrete scroll-depth milestone in the engagement funnel (ORB-374). `ThresholdPct`
 * is the milestone (25/50/75/100); `Events` counts the `scroll_depth` events that crossed it.
 * This is the milestone funnel — distinct from {@link EngagementMetricsDto.AvgMaxScrollPct},
 * the per-session deepest scroll — so the two are never double-counted.
 */
export interface ScrollDepthBucketDto {
  ThresholdPct: number
  Events: AnalyticsMetricDto
}

/**
 * Per-scope engagement metrics (ORB-374 · M2), derived from the engagement event types.
 * Time-on-page and per-session max scroll come from the once-per-pageview `page_engagement`
 * dwell summary; the scroll funnel from the discrete `scroll_depth` milestones; the
 * interaction counts from `cta_click` / `download_click` / `video_play`. Like the M1 visitor
 * metrics these are current-window only (no PreviousWindow/Delta), so the tiles render
 * without a growth pill.
 */
export interface EngagementMetricsDto {
  /** Mean time-on-page in seconds across `page_engagement` dwell records. 0 until beacons land. */
  AvgTimeOnPageSeconds: AnalyticsMetricDto
  /**
   * Mean per-session deepest scroll as a percentage (0–100), from `page_engagement.maxScrollPct`,
   * over sessions that actually scrolled. ORB-534: sessions with a real maximum of 0 are excluded —
   * averaging them in described the non-reading population rather than the readers. Read it with
   * {@link ScrolledSessions}; a high average over a handful of sessions overstates.
   */
  AvgMaxScrollPct: AnalyticsMetricDto
  /** Sessions that scrolled — the denominator behind {@link AvgMaxScrollPct}. Absent on an older API. */
  ScrolledSessions?: number
  /** Scroll funnel: `scroll_depth` milestone counts, ordered by threshold ascending. */
  ScrollDepthFunnel: ScrollDepthBucketDto[]
  /** `cta_click` event count in scope. */
  CtaClicks: AnalyticsMetricDto
  /** `download_click` event count in scope. */
  DownloadClicks: AnalyticsMetricDto
  /** `video_play` event count in scope. */
  VideoPlays: AnalyticsMetricDto
  /** Number of `page_engagement` dwell records — the denominator behind the dwell metrics. */
  PageEngagementEvents: number
}

/**
 * Composite analytics **view model** consumed by the tiles. This is NOT the raw wire shape:
 * the API nests the M1 visitor section differently (see {@link RawAnalyticsViewDto}), and
 * `normalizeAnalyticsView` (src/utils/analyticsNormalize.ts) maps wire → this shape. The P2
 * funnel fields below match the wire 1:1; the M1 fields are produced by the normalizer.
 */
export interface AnalyticsViewDto {
  // --- P2 baseline funnel (ORB-328) ---
  LandingPageViews: AnalyticsMetricDto
  /**
   * Distinct sessions that viewed the page (ORB-534). Optional: absent until the matching API
   * change is deployed to this environment, and the funnel falls back to the old Thank-You stage
   * while it is, so a dashboard that ships first degrades rather than rendering a zero.
   */
  UniqueLandingPageViews?: AnalyticsMetricDto
  ThankYouPageViews: AnalyticsMetricDto
  Conversions: AnalyticsMetricDto

  // --- M1 richer metrics (ORB-373) — optional during API rollout ---
  /** Distinct visitors across the window. */
  UniqueVisitors?: AnalyticsMetricDto
  /** Distinct visitors with a resolved ContactId. */
  UniqueKnownVisitors?: AnalyticsMetricDto
  /** Distinct visitors with no ContactId. */
  UniqueUnknownVisitors?: AnalyticsMetricDto
  /** Distinct visitors with a prior session on a persistent ID. */
  ReturningVisitors?: AnalyticsMetricDto
  /** Total visitor sessions. */
  Sessions?: AnalyticsMetricDto
  /** Conversion rate as a percentage (0–100). */
  ConversionRate?: AnalyticsMetricDto
  /**
   * Average session duration in seconds. Single-pageview (bounce) sessions read as 0
   * until M2 dwell beacons land.
   */
  AvgSessionDurationSeconds?: AnalyticsMetricDto

  /** Top traffic referrers by session count, highest first. */
  TopReferrers?: AttributionItemDto[]
  /** Top UTM campaigns by session count, highest first. */
  TopUtmCampaigns?: AttributionItemDto[]
  /** Sessions bucketed across the five marketing channels. */
  Channels?: ChannelBreakdownDto

  /** Aggregate unknown-visitor reporting. */
  Unknown?: UnknownReportingDto

  /** Engagement metrics (ORB-374 · M2): time-on-page, scroll depth, click/CTA/download/video. */
  Engagement?: EngagementMetricsDto

  /** Echoes which attribution view produced the credited numbers above. */
  AttributionView?: AttributionView
}

// --- Raw API wire shape (ORB-373) -----------------------------------------------------
// The analytics endpoints emit the M1 visitor section in a DIFFERENT shape than the tiles
// consume: visitor metrics are nested under `Visitors` as bare numbers (no window/delta),
// attribution lives under `Attribution`, and the channel/referrer/campaign breakdowns are
// flat arrays. These interfaces mirror that wire JSON; `normalizeAnalyticsView` translates
// them into the {@link AnalyticsViewDto} view model above. Do NOT consume Raw* in components.

/** Per-level visitor counts as the API emits them — bare numbers, no PreviousWindow/Delta. */
export interface RawVisitorMetricsDto {
  UniqueVisitors: number
  KnownVisitors: number
  UnknownVisitors: number
  ReturningVisitors: number
  Sessions: number
  ConversionRate: number
  AvgSessionDurationSeconds: number
}

/**
 * One breakdown row as emitted by the API (`AnalyticsBreakdownItemDto`): a bucket key and its
 * count. `Count` is converting sessions/visitors for the attribution lists, or sessions for the
 * top-N breakdowns. Channel rows carry a lowercase channel name in `Key` (e.g. `"email"`).
 */
export interface RawAttributionRowDto {
  Key?: string
  Count?: number
}

/** Attribution breakdowns + active view. `View` is a numeric enum on the wire. */
export interface RawAttributionDto {
  View: number
  ByChannel: RawAttributionRowDto[]
  BySource: RawAttributionRowDto[]
  ByCampaign: RawAttributionRowDto[]
}

/** Aggregate unknown reporting as emitted by the API — bare numbers. */
export interface RawUnknownReportingDto {
  UnknownVisitors: number
  UnknownSessions: number
  UnknownToKnownConversionRate: number
}

/**
 * Engagement metrics as the API emits them (`EngagementMetricsDto`) — bare numbers plus the
 * scroll funnel as keyed breakdown rows (ORB-374). `AvgTimeOnPageSeconds` is already in seconds
 * and `AvgMaxScrollPct` already a 0–100 percentage (no conversion in the normalizer). Funnel rows
 * carry the threshold as a numeric string in `Key` (e.g. `"50"`) and the milestone count in `Count`.
 */
export interface RawEngagementMetricsDto {
  AvgTimeOnPageSeconds: number
  AvgMaxScrollPct: number
  /** Sessions that scrolled — the denominator behind `AvgMaxScrollPct` (ORB-534). */
  ScrolledSessions?: number
  ScrollDepthFunnel: RawAttributionRowDto[]
  CtaClicks: number
  DownloadClicks: number
  VideoPlays: number
  PageEngagementEvents: number
}

/** Raw analytics payload returned by the Web / Campaign / Workspace analytics endpoints. */
export interface RawAnalyticsViewDto {
  // P2 funnel — already matches the view model.
  LandingPageViews: AnalyticsMetricDto
  /** Distinct sessions that viewed the page (ORB-534). Absent until the API change lands. */
  UniqueLandingPageViews?: AnalyticsMetricDto
  ThankYouPageViews: AnalyticsMetricDto
  Conversions: AnalyticsMetricDto
  // M1 nested sections (optional during rollout).
  Visitors?: RawVisitorMetricsDto
  Attribution?: RawAttributionDto
  Unknown?: RawUnknownReportingDto
  // M2 engagement section (optional during rollout).
  Engagement?: RawEngagementMetricsDto
  // Top-level breakdown arrays. These may duplicate `Attribution.By*`; the normalizer prefers
  // whichever is non-empty so it is correct regardless of which the API treats as canonical.
  TopReferrers?: RawAttributionRowDto[]
  TopUtmCampaigns?: RawAttributionRowDto[]
  Channels?: RawAttributionRowDto[]
  SessionsBySource?: RawAttributionRowDto[]
  SessionsByMedium?: RawAttributionRowDto[]
  SessionsByCampaign?: RawAttributionRowDto[]
}

// --- Per-Contact visitor timeline (ORB-373) -------------------------------------------

/** A single page view within a session. */
export interface ContactPageViewDto {
  Url: string
  Title?: string | null
  ViewedAtUtc: string // ISO8601
}

/** One visitor session for a Contact, with its page-view detail. */
export interface ContactSessionDto {
  SessionId: string
  StartedAtUtc: string // ISO8601
  EndedAtUtc?: string | null // ISO8601
  /** Session duration in seconds; 0 for single-pageview (bounce) sessions until M2. */
  DurationSeconds: number
  /** First-touch source (utm_source or referrer host). */
  Source?: string | null
  Medium?: string | null
  Campaign?: string | null
  Channel?: AnalyticsChannel | null
  Converted: boolean
  PageViews: ContactPageViewDto[]
}

/** Rolled-up visitor stats for a Contact across all sessions. */
export interface ContactVisitorRollupDto {
  /** Total page views across all sessions. */
  TotalVisits: number
  TotalSessions: number
  FirstSeenUtc?: string | null // ISO8601
  LastSeenUtc?: string | null // ISO8601
  ConversionCount: number
  /** Most-visited landing pages, highest first. */
  TopLandingPages: AttributionItemDto[]
}

/** Per-Contact visitor timeline + rollups returned by the Contact timeline endpoint. */
export interface ContactVisitorTimelineDto {
  ContactId: string
  Rollup: ContactVisitorRollupDto
  Sessions: ContactSessionDto[]
}
